import { z } from 'zod';

// =============================================================================
// Enums (mirror Prisma enums in apps/api)
// =============================================================================

export const RessoClasificacionEnum = z.enum(['aceptable', 'moderado', 'inaceptable']);
export type RessoClasificacion = z.infer<typeof RessoClasificacionEnum>;

export const RessoWorkerEstadoEnum = z.enum(['PENDIENTE', 'CUMPLE', 'NO_CUMPLE', 'NA']);
export type RessoWorkerEstado = z.infer<typeof RessoWorkerEstadoEnum>;

export const RessoEvaluationStatusEnum = z.enum(['BORRADOR', 'EN_PROGRESO', 'COMPLETADA']);
export type RessoEvaluationStatus = z.infer<typeof RessoEvaluationStatusEnum>;

export const RessoDocumentTipoEnum = z.enum([
  'CONTRATO',
  'CERTIFICACION',
  'ODI',
  'CAPACITACION',
  'OTRO',
]);
export type RessoDocumentTipo = z.infer<typeof RessoDocumentTipoEnum>;

export const RessoDocumentStatusEnum = z.enum(['VIGENTE', 'VENCIDO', 'RECHAZADO']);
export type RessoDocumentStatus = z.infer<typeof RessoDocumentStatusEnum>;

export const RessoAlertTipoEnum = z.enum(['CRITICO', 'EN_RIESGO']);
export type RessoAlertTipo = z.infer<typeof RessoAlertTipoEnum>;

export const RessoEvidenciaTypeEnum = z.enum(['DOCUMENT', 'CHARLA', 'MANUAL']);
export type RessoEvidenciaType = z.infer<typeof RessoEvidenciaTypeEnum>;

// =============================================================================
// Evaluation request schemas
// =============================================================================

export const RessoEvaluationCreateSchema = z.object({
  nombre: z.string().min(1).max(200),
  period: z.string().max(20).optional(),
  antecedentes: z.record(z.string(), z.unknown()).optional(),
});
export type RessoEvaluationCreateInput = z.infer<typeof RessoEvaluationCreateSchema>;

export const RessoEvaluationUpdateSchema = z.object({
  nombre: z.string().min(1).max(200).optional(),
  status: RessoEvaluationStatusEnum.optional(),
  period: z.string().max(20).optional(),
  antecedentes: z.record(z.string(), z.unknown()).optional(),
  documentalData: z.record(z.string(), z.unknown()).optional(),
  terrenoData: z.record(z.string(), z.unknown()).optional(),
  observaciones: z.string().optional(),
  firmas: z.array(z.unknown()).optional(),
});
export type RessoEvaluationUpdateInput = z.infer<typeof RessoEvaluationUpdateSchema>;

// =============================================================================
// Worker request schemas
// =============================================================================

export const RessoWorkerAddSchema = z.object({
  usuarioId: z.number().int().positive().optional(),
  nombre: z.string().min(1).max(200),
  rut: z.string().min(1).max(20),
  cargo: z.string().max(200).optional(),
});
export type RessoWorkerAddInput = z.infer<typeof RessoWorkerAddSchema>;

export const RessoWorkerResponseUpsertSchema = z.object({
  responses: z
    .array(
      z.object({
        questionId: z.string().min(1),
        estado: RessoWorkerEstadoEnum,
        evidencia: z.string().optional(),
        evidenciaType: RessoEvidenciaTypeEnum.optional(),
        fecha: z.string().datetime().optional(),
      }),
    )
    .min(1),
});
export type RessoWorkerResponseUpsertInput = z.infer<typeof RessoWorkerResponseUpsertSchema>;

// =============================================================================
// Document request schemas (presign + confirm flow per SIME upload pattern)
// =============================================================================

export const RessoDocumentPresignSchema = z.object({
  evaluationId: z.number().int().positive(),
  ressoWorkerId: z.number().int().positive().optional(),
  questionId: z.string().optional(),
  tipo: RessoDocumentTipoEnum,
  filename: z.string().min(1).max(255),
  mimeType: z
    .string()
    .regex(/^(application\/pdf|image\/(jpeg|png|webp))$/, 'Solo PDF, JPG, PNG, WEBP'),
  fileSize: z.number().int().positive().max(20 * 1024 * 1024, 'Max 20 MB'),
});
export type RessoDocumentPresignInput = z.infer<typeof RessoDocumentPresignSchema>;

// HIGH H1 fix (auditor pass 3): apply the same mimeType whitelist as presign.
// Previously confirm accepted any string, allowing a client to register a
// row with an arbitrary mimeType after the upload (e.g. application/octet-stream
// from some browsers) — which the dashboard could later embed as <embed type=...>.
const ALLOWED_MIME_TYPES_REGEX = /^(application\/pdf|image\/(jpeg|png|webp))$/;

export const RessoDocumentConfirmSchema = z.object({
  s3Key: z.string().min(1),
  evaluationId: z.number().int().positive(),
  ressoWorkerId: z.number().int().positive().optional(),
  questionId: z.string().optional(),
  tipo: RessoDocumentTipoEnum,
  filename: z.string().min(1).max(255),
  mimeType: z.string().regex(ALLOWED_MIME_TYPES_REGEX, 'Solo PDF, JPG, PNG, WEBP'),
  fileSize: z.number().int().positive(),
  fechaEmision: z.string().datetime().optional(),
  fechaVencimiento: z.string().datetime().optional(),
});
export type RessoDocumentConfirmInput = z.infer<typeof RessoDocumentConfirmSchema>;

// =============================================================================
// Response shapes (DTOs)
// =============================================================================

export const RessoEvaluationDTOSchema = z.object({
  id: z.number(),
  empresaId: z.number(),
  nombre: z.string(),
  status: RessoEvaluationStatusEnum,
  period: z.string().nullable(),
  scoreDocumental: z.number().nullable(),
  scoreTerreno: z.number().nullable(),
  scoreFinal: z.number().nullable(),
  clasificacion: RessoClasificacionEnum.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RessoEvaluationDTO = z.infer<typeof RessoEvaluationDTOSchema>;

export const RessoAlertAcknowledgeSchema = z.object({
  alertId: z.number().int().positive(),
});
export type RessoAlertAcknowledgeInput = z.infer<typeof RessoAlertAcknowledgeSchema>;
